'use client';

import { useState } from 'react';
import {
    Banknote,
    TrendingUp,
    Filter,
    Search,
    ChevronDown,
    Calendar,
    ArrowUpRight,
    User,
    FileText,
    Plus,
    Eye,
    Edit,
    Trash,
    CheckCircle2,
    XCircle,
    X,
    Receipt,
    History,
    FileCheck,
    Download,
    AlertCircle,
    CreditCard,
    Wallet,
    ArrowRight
} from 'lucide-react';
import Link from 'next/link';

// --- Types ---

type ExpenditureStatus = 'Draft' | 'Menunggu Pembayaran' | 'Selesai / Dibayar' | 'Ditolak';
type ExpenditureCategory = 'ATK' | 'Operasional' | 'Sarana & Prasarana' | 'Kegiatan' | 'Honor / Jasa' | 'Lainnya';
type PaymentMethod = 'Tunai' | 'Transfer';
type SourceOfFunds = 'Kas' | 'Rekening Bank';

interface AuditLogEntry {
    time: string;
    user: string;
    action: string;
    note?: string;
}

interface JournalEntry {
    account: string;
    debit: number;
    credit: number;
}

interface ExpenditureJournal {
    id: string;
    date: string;
    entries: JournalEntry[];
}

interface Expenditure {
    id: string;
    date: string;
    title: string;
    category: ExpenditureCategory;
    vendor: string;
    source: SourceOfFunds;
    paymentMethod: PaymentMethod;
    amount: number;
    description: string;
    status: ExpenditureStatus;
    proofDocument?: string;
    rejectReason?: string;
    journal?: ExpenditureJournal;
    auditLog: AuditLogEntry[];
}

// --- Mock Data ---

const INITIAL_EXPENDITURES: Expenditure[] = [
    {
        id: 'EXP-2024-001',
        date: '2024-02-09',
        title: 'Pembelian Kertas A4 & Tinta Printer',
        category: 'ATK',
        vendor: 'CV. Maju Jaya Stationery',
        source: 'Kas',
        paymentMethod: 'Tunai',
        amount: 450000,
        description: 'Pembelian stok ATK untuk kantor guru dan tata usaha.',
        status: 'Selesai / Dibayar',
        proofDocument: 'nota_atk_feb.pdf',
        journal: {
            id: 'JRN-2024-001',
            date: '2024-02-09',
            entries: [
                { account: 'Beban ATK', debit: 450000, credit: 0 },
                { account: 'Kas Tunai', debit: 0, credit: 450000 }
            ]
        },
        auditLog: [
            { time: '2024-02-09 09:00', user: 'Admin Keuangan', action: 'Membuat Pengeluaran' },
            { time: '2024-02-09 10:30', user: 'Admin Keuangan', action: 'Konfirmasi Pembayaran' }
        ]
    },
    {
        id: 'EXP-2024-002',
        date: '2024-02-08',
        title: 'Pembayaran Listrik & Air - Januari',
        category: 'Operasional',
        vendor: 'PLN & PDAM',
        source: 'Rekening Bank',
        paymentMethod: 'Transfer',
        amount: 2350000,
        description: 'Tagihan bulanan operasional sekolah.',
        status: 'Menunggu Pembayaran',
        auditLog: [
            { time: '2024-02-08 14:00', user: 'Admin Keuangan', action: 'Mengajukan Pembayaran' }
        ]
    },
    {
        id: 'EXP-2024-003',
        date: '2024-02-10',
        title: 'Service AC Ruang Kelas (3 Unit)',
        category: 'Sarana & Prasarana',
        vendor: 'Bintang AC Service',
        source: 'Kas',
        paymentMethod: 'Tunai',
        amount: 600000,
        description: 'Pemeliharaan rutin sarana kelas.',
        status: 'Draft',
        auditLog: [
            { time: '2024-02-09 16:30', user: 'Admin Keuangan', action: 'Draft Pengeluaran' }
        ]
    },
    {
        id: 'EXP-2024-004',
        date: '2024-02-05',
        title: 'Sewa Bus Outbound Siswa',
        category: 'Kegiatan',
        vendor: 'PO. Trans Wisata',
        source: 'Rekening Bank',
        paymentMethod: 'Transfer',
        amount: 5000000,
        description: 'Panjar sewa bus untuk kegiatan luar sekolah.',
        status: 'Ditolak',
        rejectReason: 'Nominal tidak sesuai dengan plafon anggaran kegiatan.',
        auditLog: [
            { time: '2024-02-05 10:00', user: 'Admin Keuangan', action: 'Mengajukan Pembayaran' },
            { time: '2024-02-05 11:30', user: 'Kepala Sekolah', action: 'Menolak Pengajuan', note: 'Anggaran melebihi batas.' }
        ]
    }
];

export default function PengeluaranBiayaPage() {
    // --- State ---
    const [expenditures, setExpenditures] = useState<Expenditure[]>(INITIAL_EXPENDITURES);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [methodFilter, setMethodFilter] = useState<string>('all');
    const [periodFilter, setPeriodFilter] = useState<string>('all');

    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isPayModalOpen, setIsPayModalOpen] = useState(false);

    const [selectedExpenditure, setSelectedExpenditure] = useState<Expenditure | null>(null);
    const [viewingJournal, setViewingJournal] = useState(false);

    // --- Helpers ---
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const getStatusStyles = (status: ExpenditureStatus) => {
        switch (status) {
            case 'Selesai / Dibayar':
                return 'bg-success-soft text-success border-success/20';
            case 'Menunggu Pembayaran':
                return 'bg-warning-soft text-warning border-warning/20';
            case 'Draft':
                return 'bg-muted/10 text-muted border-muted/20';
            case 'Ditolak':
                return 'bg-error-soft text-error border-error/20';
            default:
                return 'bg-secondary-soft text-secondary border-secondary/20';
        }
    };

    const getCategoryIcon = (category: ExpenditureCategory) => {
        switch (category) {
            case 'ATK': return <FileText className="w-4 h-4" />;
            case 'Operasional': return <TrendingUp className="w-4 h-4" />;
            case 'Sarana & Prasarana': return <ArrowUpRight className="w-4 h-4" />;
            case 'Kegiatan': return <Calendar className="w-4 h-4" />;
            case 'Honor / Jasa': return <User className="w-4 h-4" />;
            default: return <AlertCircle className="w-4 h-4" />;
        }
    };

    // --- Action Handlers ---
    const handleAddClick = () => {
        setSelectedExpenditure(null);
        setIsFormModalOpen(true);
    };

    const handleEditClick = (exp: Expenditure) => {
        setSelectedExpenditure(exp);
        setIsFormModalOpen(true);
    };

    const handleDeleteClick = (exp: Expenditure) => {
        setSelectedExpenditure(exp);
        setIsDeleteModalOpen(true);
    };

    const handleDetailClick = (exp: Expenditure) => {
        setSelectedExpenditure(exp);
        setViewingJournal(false);
        setIsDetailModalOpen(true);
    };

    const handlePayClick = (exp: Expenditure) => {
        setSelectedExpenditure(exp);
        setIsPayModalOpen(true);
    };

    const handleStatusMove = (id: string, newStatus: ExpenditureStatus, note?: string) => {
        setExpenditures(prev => prev.map(item => {
            if (item.id === id) {
                const newLogEntry: AuditLogEntry = {
                    time: new Date().toLocaleString(),
                    user: 'Admin Keuangan', // Mock user
                    action: `Ubah status ke ${newStatus}`,
                    note
                };

                const updatedItem = { ...item, status: newStatus, auditLog: [...item.auditLog, newLogEntry] };

                // Auto-generate journal if finished
                if (newStatus === 'Selesai / Dibayar') {
                    updatedItem.journal = {
                        id: `JRN-${id.split('-')[2]}`,
                        date: new Date().toISOString().split('T')[0],
                        entries: [
                            { account: `Beban ${item.category}`, debit: item.amount, credit: 0 },
                            { account: item.source === 'Kas' ? 'Kas Tunai' : 'Bank Mandiri', debit: 0, credit: item.amount }
                        ]
                    };
                }

                return updatedItem;
            }
            return item;
        }));
        setIsPayModalOpen(false);
        setIsFormModalOpen(false);
    };

    // --- Filtered Data ---
    const filteredData = expenditures.filter(item => {
        const matchesSearch =
            item.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.vendor.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
        const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
        const matchesMethod = methodFilter === 'all' || item.paymentMethod === methodFilter;

        return matchesSearch && matchesStatus && matchesCategory && matchesMethod;
    });

    const summary = {
        total: expenditures.filter(e => e.status === 'Selesai / Dibayar').reduce((acc, curr) => acc + curr.amount, 0),
        count: expenditures.length,
        pending: expenditures.filter(e => e.status === 'Menunggu Pembayaran').length,
        finished: expenditures.filter(e => e.status === 'Selesai / Dibayar').length
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700 relative">
            {/* Header Area */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="relative z-10 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                                <Link href="/sekolah" className="hover:text-primary transition-all">Keuangan</Link>
                                <span className="text-muted/50">•</span>
                                <span className="text-foreground">Realisasi Pengeluaran</span>
                            </div>

                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                    <Banknote className="w-8 h-8" />
                                </div>
                                <div className="space-y-1">
                                    <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight whitespace-nowrap">Pengeluaran Biaya</h1>
                                    <p className="text-secondary font-medium flex items-center gap-2 text-sm">
                                        <TrendingUp className="w-3.5 h-3.5 text-success" /> Realisasi Pengeluaran Operasional Sekolah • PAUD Al-Azhar 1
                                    </p>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleAddClick}
                            className="flex items-center gap-3 px-6 py-3.5 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all"
                        >
                            <Plus className="w-4 h-4" /> Input Pengeluaran Baru
                        </button>
                    </div>
                </div>
            </section>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="p-6 rounded-3xl bg-background border border-border-main shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Total Pengeluaran</span>
                        <div className="p-2 bg-primary-soft/30 rounded-lg text-primary"><TrendingUp className="w-4 h-4" /></div>
                    </div>
                    <p className="text-2xl font-black text-foreground">{formatCurrency(summary.total)}</p>
                    <p className="text-[10px] text-muted font-medium">Bulan berjalan</p>
                </div>
                <div className="p-6 rounded-3xl bg-background border border-border-main shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Jumlah Transaksi</span>
                        <div className="p-2 bg-secondary-soft/30 rounded-lg text-secondary"><FileText className="w-4 h-4" /></div>
                    </div>
                    <p className="text-2xl font-black text-foreground">{summary.count} <span className="text-sm font-medium text-muted">Trx</span></p>
                    <p className="text-[10px] text-muted font-medium">Total periode ini</p>
                </div>
                <div className="p-6 rounded-3xl bg-background border border-border-main shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Menunggu Pembayaran</span>
                        <div className="p-2 bg-warning-soft/30 rounded-lg text-warning"><Receipt className="w-4 h-4" /></div>
                    </div>
                    <p className="text-2xl font-black text-foreground">{summary.pending} <span className="text-sm font-medium text-muted">Trx</span></p>
                    <p className="text-[10px] text-muted font-medium">Perlu tindakan</p>
                </div>
                <div className="p-6 rounded-3xl bg-background border border-success/10 shadow-sm flex flex-col gap-4 text-success">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Pengeluaran Selesai</span>
                        <div className="p-2 bg-success-soft/30 rounded-lg text-success"><FileCheck className="w-4 h-4" /></div>
                    </div>
                    <p className="text-2xl font-black text-foreground">{summary.finished} <span className="text-sm font-medium text-muted">Terbayar</span></p>
                    <p className="text-[10px] text-muted font-medium">Sudah terjurnal</p>
                </div>
            </div>

            {/* Filter & Search */}
            <div className="bg-surface border border-border-main p-4 rounded-3xl shadow-sm space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1 group w-full md:max-w-xs">
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted group-focus-within:text-primary transition-colors" />
                        <input
                            type="text"
                            placeholder="Cari ID / Judul / Vendor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-background/50 border border-border-main rounded-2xl py-3.5 pl-12 pr-6 text-xs font-semibold focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all outline-none"
                        />
                    </div>

                    <div className="flex flex-wrap gap-3 flex-1">
                        <div className="relative group min-w-[140px]">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full pl-4 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest outline-none appearance-none cursor-pointer hover:border-primary/30 transition-all text-muted"
                            >
                                <option value="all">Semua Status</option>
                                <option value="Draft">Draft</option>
                                <option value="Menunggu Pembayaran">Menunggu Pembayaran</option>
                                <option value="Selesai / Dibayar">Selesai / Dibayar</option>
                                <option value="Ditolak">Ditolak</option>
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
                        </div>

                        <div className="relative group min-w-[150px]">
                            <select
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                className="w-full pl-4 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest outline-none appearance-none cursor-pointer hover:border-primary/30 transition-all text-muted"
                            >
                                <option value="all">Semua Kategori</option>
                                <option value="ATK">ATK</option>
                                <option value="Operasional">Operasional</option>
                                <option value="Sarana & Prasarana">Sarana & Prasarana</option>
                                <option value="Kegiatan">Kegiatan</option>
                                <option value="Honor / Jasa">Honor / Jasa</option>
                                <option value="Lainnya">Lainnya</option>
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
                        </div>

                        <div className="relative group min-w-[140px]">
                            <select
                                value={methodFilter}
                                onChange={(e) => setMethodFilter(e.target.value)}
                                className="w-full pl-4 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest outline-none appearance-none cursor-pointer hover:border-primary/30 transition-all text-muted"
                            >
                                <option value="all">Semua Metode</option>
                                <option value="Tunai">Tunai</option>
                                <option value="Transfer">Transfer</option>
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
                        </div>

                        <button
                            onClick={() => setPeriodFilter(periodFilter === 'all' ? 'thisMonth' : 'all')}
                            className={`flex items-center gap-3 px-5 py-3.5 border rounded-2xl text-[10px] font-bold uppercase tracking-widest transition-all ${periodFilter === 'thisMonth'
                                ? 'bg-primary text-white border-primary'
                                : 'bg-background border-border-main text-muted hover:border-primary/30'
                                }`}
                        >
                            <Calendar className="w-4 h-4" />
                            {periodFilter === 'thisMonth' ? 'Bulan Ini' : 'Pilih Periode'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-surface border border-border-main rounded-3xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[160px]">ID & Tanggal</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Judul Pengeluaran & Vendor</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[140px]">Kategori</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[150px]">Sumber Dana</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[160px] text-right">Nominal</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[180px] text-center">Status</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[160px] text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main/50">
                            {filteredData.length > 0 ? filteredData.map((item) => (
                                <tr key={item.id} className="hover:bg-primary-soft/5 transition-colors group">
                                    <td className="px-6 py-4">
                                        <div>
                                            <p className="text-xs font-black text-primary group-hover:underline cursor-pointer">{item.id}</p>
                                            <div className="flex items-center gap-1.5 mt-1 text-muted">
                                                <Calendar className="w-3 h-3" />
                                                <span className="text-[10px] font-bold uppercase">{item.date}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 space-y-1">
                                        <p className="text-xs font-bold text-foreground line-clamp-1">{item.title}</p>
                                        <p className="text-[10px] text-muted flex items-center gap-1.5 font-medium uppercase tracking-wider">
                                            <User className="w-3 h-3" /> {item.vendor}
                                        </p>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-background border border-border-main text-[9px] font-bold text-secondary uppercase tracking-wider">
                                            {getCategoryIcon(item.category)}
                                            {item.category}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="text-xs font-bold text-foreground">{item.source}</span>
                                            <span className="text-[10px] text-muted uppercase font-medium">{item.paymentMethod}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <p className="text-sm font-black text-foreground tabular-nums">{formatCurrency(item.amount)}</p>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${getStatusStyles(item.status)}`}>
                                            {item.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            {item.status === 'Draft' && (
                                                <>
                                                    <button onClick={() => handleEditClick(item)} className="p-2 rounded-xl text-muted hover:text-warning hover:bg-warning-soft/30 transition-all" title="Edit"><Edit className="w-4 h-4" /></button>
                                                    <button onClick={() => handleDeleteClick(item)} className="p-2 rounded-xl text-muted hover:text-error hover:bg-error-soft/30 transition-all" title="Hapus"><Trash className="w-4 h-4" /></button>
                                                    <button onClick={() => handleStatusMove(item.id, 'Menunggu Pembayaran')} className="p-2 rounded-xl text-primary hover:bg-primary-soft/30 transition-all" title="Ajukan Pembayaran"><ArrowUpRight className="w-4 h-4" /></button>
                                                </>
                                            )}
                                            {item.status === 'Menunggu Pembayaran' && (
                                                <>
                                                    <button onClick={() => handlePayClick(item)} className="px-3 py-1.5 rounded-xl bg-success text-white text-[10px] font-bold uppercase tracking-wider hover:shadow-lg hover:shadow-success/30 transition-all">Bayar</button>
                                                    <button onClick={() => handleDetailClick(item)} className="p-2 rounded-xl text-muted hover:text-primary hover:bg-primary-soft/30 transition-all"><Eye className="w-4 h-4" /></button>
                                                </>
                                            )}
                                            {item.status === 'Selesai / Dibayar' && (
                                                <button onClick={() => handleDetailClick(item)} className="px-3 py-1.5 rounded-xl bg-background border border-border-main text-[10px] font-bold text-muted hover:text-primary hover:border-primary/50 transition-all uppercase tracking-wider">
                                                    Detail
                                                </button>
                                            )}
                                            {item.status === 'Ditolak' && (
                                                <button onClick={() => handleDetailClick(item)} className="p-2 rounded-xl text-error bg-error-soft/30 hover:bg-error-soft/50 transition-all" title="Lihat Alasan"><AlertCircle className="w-4 h-4" /></button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-10 text-center text-muted font-medium italic">
                                        Tidak ada data pengeluaran ditemukan
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- Modals Componentized for Brevity in Code --- */}

            {/* Delete Modal */}
            {isDeleteModalOpen && selectedExpenditure && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface p-8 rounded-[32px] shadow-2xl max-w-sm w-full space-y-6 animate-in zoom-in-95 duration-200">
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 bg-error-soft text-error rounded-full flex items-center justify-center mx-auto shadow-inner">
                                <Trash className="w-8 h-8" />
                            </div>
                            <div className="space-y-1">
                                <h3 className="text-xl font-bold text-foreground">Hapus Pengeluaran?</h3>
                                <p className="text-sm text-muted font-medium">Anda yakin ingin menghapus <b>{selectedExpenditure.id}</b>? Tindakan ini tidak dapat dibatalkan.</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => setIsDeleteModalOpen(false)} className="flex-1 px-4 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-secondary-soft/50 transition-all">Batal</button>
                            <button onClick={() => { setExpenditures(prev => prev.filter(e => e.id !== selectedExpenditure.id)); setIsDeleteModalOpen(false); }} className="flex-1 px-4 py-3.5 bg-error text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:shadow-xl hover:shadow-error/20 transition-all">Ya, Hapus</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Form Modal (Add/Edit) */}
            {isFormModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface rounded-[40px] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom-8 duration-300">
                        <div className="p-8 border-b border-border-main flex items-center justify-between bg-background/50">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-primary-soft rounded-2xl flex items-center justify-center text-primary">
                                    <Plus className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-foreground">{selectedExpenditure ? 'Edit Pengeluaran' : 'Input Pengeluaran Baru'}</h3>
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Detail Realisasi Biaya</p>
                                </div>
                            </div>
                            <button onClick={() => setIsFormModalOpen(false)} className="p-2 rounded-full hover:bg-secondary-soft/50 text-muted transition-all"><X className="w-5 h-5" /></button>
                        </div>

                        <div className="p-8 overflow-y-auto space-y-6 flex-1 custom-scrollbar">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-tighter ml-1">Tanggal Pengeluaran</label>
                                    <div className="relative group">
                                        <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted group-focus-within:text-primary transition-colors" />
                                        <input type="date" defaultValue={selectedExpenditure?.date || new Date().toISOString().split('T')[0]} className="w-full bg-background border border-border-main rounded-2xl py-3.5 pl-12 pr-6 text-sm font-semibold outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-tighter ml-1">Kategori Biaya</label>
                                    <div className="relative">
                                        <select defaultValue={selectedExpenditure?.category} className="w-full bg-background border border-border-main rounded-2xl py-3.5 px-6 text-sm font-semibold outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all appearance-none cursor-pointer">
                                            <option>ATK</option>
                                            <option>Operasional</option>
                                            <option>Sarana & Prasarana</option>
                                            <option>Kegiatan</option>
                                            <option>Honor / Jasa</option>
                                            <option>Lainnya</option>
                                        </select>
                                        <ChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                                    </div>
                                </div>
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-tighter ml-1">Judul Pengeluaran</label>
                                    <input type="text" placeholder="Contoh: Pembelian Alat Tulis Kantor" defaultValue={selectedExpenditure?.title} className="w-full bg-background border border-border-main rounded-2xl py-3.5 px-6 text-sm font-semibold outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-tighter ml-1">Vendor / Penerima</label>
                                    <input type="text" placeholder="Nama Toko / Instansi / Orang" defaultValue={selectedExpenditure?.vendor} className="w-full bg-background border border-border-main rounded-2xl py-3.5 px-6 text-sm font-semibold outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-tighter ml-1">Nominal (Rp)</label>
                                    <input type="number" placeholder="0" defaultValue={selectedExpenditure?.amount} className="w-full bg-background border border-border-main rounded-2xl py-3.5 px-6 text-sm font-black text-primary outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-tighter ml-1">Sumber Dana</label>
                                    <div className="flex gap-4">
                                        <label className="flex-1 cursor-pointer">
                                            <input type="radio" name="source" value="Kas" defaultChecked={selectedExpenditure?.source === 'Kas' || !selectedExpenditure} className="peer hidden" />
                                            <div className="flex items-center justify-center gap-2 p-3.5 rounded-2xl border border-border-main bg-background text-muted peer-checked:bg-primary peer-checked:text-white peer-checked:border-primary transition-all font-bold text-xs uppercase tracking-widest shadow-sm">
                                                <Wallet className="w-4 h-4" /> Kas
                                            </div>
                                        </label>
                                        <label className="flex-1 cursor-pointer">
                                            <input type="radio" name="source" value="Rekening Bank" defaultChecked={selectedExpenditure?.source === 'Rekening Bank'} className="peer hidden" />
                                            <div className="flex items-center justify-center gap-2 p-3.5 rounded-2xl border border-border-main bg-background text-muted peer-checked:bg-primary peer-checked:text-white peer-checked:border-primary transition-all font-bold text-xs uppercase tracking-widest shadow-sm">
                                                <CreditCard className="w-4 h-4" /> Bank
                                            </div>
                                        </label>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-tighter ml-1">Metode Pembayaran</label>
                                    <div className="flex gap-4">
                                        <label className="flex-1 cursor-pointer">
                                            <input type="radio" name="method" value="Tunai" defaultChecked={selectedExpenditure?.paymentMethod === 'Tunai' || !selectedExpenditure} className="peer hidden" />
                                            <div className="flex items-center justify-center gap-2 p-3.5 rounded-2xl border border-border-main bg-background text-muted peer-checked:bg-secondary peer-checked:text-white peer-checked:border-secondary transition-all font-bold text-xs uppercase tracking-widest shadow-sm">
                                                Tunai
                                            </div>
                                        </label>
                                        <label className="flex-1 cursor-pointer">
                                            <input type="radio" name="method" value="Transfer" defaultChecked={selectedExpenditure?.paymentMethod === 'Transfer'} className="peer hidden" />
                                            <div className="flex items-center justify-center gap-2 p-3.5 rounded-2xl border border-border-main bg-background text-muted peer-checked:bg-secondary peer-checked:text-white peer-checked:border-secondary transition-all font-bold text-xs uppercase tracking-widest shadow-sm">
                                                Transfer
                                            </div>
                                        </label>
                                    </div>
                                </div>
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-tighter ml-1">Keterangan Tambahan</label>
                                    <textarea rows={3} placeholder="Detail peruntukan dana..." defaultValue={selectedExpenditure?.description} className="w-full bg-background border border-border-main rounded-2xl p-6 text-sm font-semibold outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all resize-none"></textarea>
                                </div>
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-tighter ml-1">Upload Bukti (Nota/Invoice)</label>
                                    <div className="border-2 border-dashed border-border-main rounded-3xl p-8 text-center hover:border-primary/50 transition-all group cursor-pointer">
                                        <div className="w-12 h-12 bg-background rounded-full flex items-center justify-center mx-auto mb-4 text-muted group-hover:text-primary transition-all">
                                            <ArrowUpRight className="w-6 h-6 rotate-45" />
                                        </div>
                                        <p className="text-xs font-bold text-foreground">Klik untuk upload atau drag and drop</p>
                                        <p className="text-[10px] text-muted font-medium mt-1 uppercase tracking-widest">PNG, JPG, PDF (Max 5MB)</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-8 bg-background/50 border-t border-border-main flex gap-4">
                            <button onClick={() => setIsFormModalOpen(false)} className="px-8 py-4 bg-background border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-secondary-soft/50 transition-all flex-1">Batal</button>
                            <button onClick={() => {
                                // Simple mock save logic
                                if (!selectedExpenditure) {
                                    const newExp: Expenditure = {
                                        id: `EXP-2024-00${expenditures.length + 1}`,
                                        date: new Date().toISOString().split('T')[0],
                                        title: 'New Expenditure',
                                        category: 'Lainnya',
                                        vendor: 'Vendor Name',
                                        source: 'Kas',
                                        paymentMethod: 'Tunai',
                                        amount: 0,
                                        description: '',
                                        status: 'Draft',
                                        auditLog: [{ time: new Date().toLocaleString(), user: 'Admin Keuangan', action: 'Membuat Pengeluaran' }]
                                    };
                                    setExpenditures([newExp, ...expenditures]);
                                }
                                setIsFormModalOpen(false);
                            }} className="px-8 py-4 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all flex-1">Simpan Data</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail & Journal Modal */}
            {isDetailModalOpen && selectedExpenditure && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface rounded-4xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col md:flex-row animate-in zoom-in-95 duration-300">
                        {/* Left Side: Info */}
                        <div className={`flex-1 flex flex-col ${viewingJournal ? 'hidden md:flex' : 'flex'}`}>
                            <div className="p-8 border-b border-border-main flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-inner ${getStatusStyles(selectedExpenditure.status)}`}>
                                        <FileText className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black text-foreground">Detail Pengeluaran</h3>
                                        <p className="text-[10px] font-black text-primary uppercase tracking-widest">{selectedExpenditure.id}</p>
                                    </div>
                                </div>
                                <button onClick={() => setIsDetailModalOpen(false)} className="md:hidden p-2 rounded-full hover:bg-secondary-soft/50 text-muted transition-all"><X className="w-5 h-5" /></button>
                            </div>

                            <div className="p-8 overflow-y-auto space-y-8 flex-1 custom-scrollbar">
                                <div className="grid grid-cols-2 gap-8">
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-wider">Tanggal</label>
                                        <p className="text-sm font-bold text-foreground">{selectedExpenditure.date}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-wider">Kategori</label>
                                        <p className="text-sm font-bold text-foreground flex items-center gap-2">
                                            {getCategoryIcon(selectedExpenditure.category)} {selectedExpenditure.category}
                                        </p>
                                    </div>
                                    <div className="col-span-2 space-y-1">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-wider">Judul / Perihal</label>
                                        <p className="text-base font-black text-foreground">{selectedExpenditure.title}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-wider">Penerima / Vendor</label>
                                        <p className="text-sm font-bold text-foreground">{selectedExpenditure.vendor}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-wider">Metode & Sumber</label>
                                        <p className="text-sm font-bold text-foreground uppercase tracking-tighter">{selectedExpenditure.paymentMethod} • {selectedExpenditure.source}</p>
                                    </div>
                                    <div className="col-span-2 space-y-1">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-wider">Nominal Realisasi</label>
                                        <p className="text-3xl font-black text-primary tabular-nums">{formatCurrency(selectedExpenditure.amount)}</p>
                                    </div>
                                    <div className="col-span-2 space-y-1">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-wider">Keterangan</label>
                                        <p className="text-xs text-secondary font-medium leading-relaxed bg-background/50 p-4 rounded-2xl border border-border-main">{selectedExpenditure.description}</p>
                                    </div>
                                    {selectedExpenditure.status === 'Ditolak' && (
                                        <div className="col-span-2 p-4 bg-error-soft/30 border border-error/20 rounded-2xl space-y-1">
                                            <div className="flex items-center gap-2 text-error">
                                                <XCircle className="w-4 h-4" />
                                                <span className="text-[10px] font-black uppercase">Alasan Penolakan</span>
                                            </div>
                                            <p className="text-[11px] font-bold text-error leading-relaxed">{selectedExpenditure.rejectReason}</p>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-4 pt-4 border-t border-border-main">
                                    <div className="flex items-center justify-between">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-wider">Bukti Dokumen</label>
                                        {selectedExpenditure.proofDocument && (
                                            <button className="text-xs font-bold text-primary flex items-center gap-1 hover:underline">
                                                <Download className="w-3.5 h-3.5" /> Unduh PDF
                                            </button>
                                        )}
                                    </div>
                                    <div className="aspect-video bg-background border border-border-main rounded-3xl flex items-center justify-center overflow-hidden relative group">
                                        {selectedExpenditure.proofDocument ? (
                                            <div className="text-center space-y-2">
                                                <Receipt className="w-12 h-12 text-muted mx-auto" />
                                                <p className="text-[10px] font-bold text-muted uppercase italic">{selectedExpenditure.proofDocument}</p>
                                            </div>
                                        ) : (
                                            <div className="text-center space-y-2 text-muted">
                                                <AlertCircle className="w-10 h-10 mx-auto opacity-20" />
                                                <p className="text-[10px] font-black uppercase tracking-widest opacity-50">Belum Ada Bukti</p>
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                                            <button className="px-6 py-2.5 bg-white text-black rounded-xl text-[10px] font-black uppercase tracking-widest shadow-xl">Lihat Preview</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side: Audit Log & Journal Toggle */}
                        <div className={`w-full md:w-[320px] bg-background/50 border-l border-border-main flex flex-col ${viewingJournal ? 'flex' : 'hidden md:flex'}`}>
                            <div className="p-8 border-b border-border-main bg-surface/50">
                                <div className="flex bg-background p-1.5 rounded-2xl border border-border-main">
                                    <button
                                        onClick={() => setViewingJournal(false)}
                                        className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${!viewingJournal ? 'bg-white shadow-sm text-primary' : 'text-muted hover:text-foreground'}`}
                                    >
                                        Audit Log
                                    </button>
                                    <button
                                        onClick={() => setViewingJournal(true)}
                                        disabled={selectedExpenditure.status !== 'Selesai / Dibayar'}
                                        className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all disabled:opacity-30 ${viewingJournal ? 'bg-white shadow-sm text-primary' : 'text-muted hover:text-foreground'}`}
                                    >
                                        Jurnal
                                    </button>
                                </div>
                            </div>

                            <div className="p-8 flex-1 overflow-y-auto custom-scrollbar">
                                {!viewingJournal ? (
                                    <div className="space-y-6">
                                        {selectedExpenditure.auditLog.map((log, i) => (
                                            <div key={i} className="flex gap-4 relative">
                                                {i !== selectedExpenditure.auditLog.length - 1 && (
                                                    <div className="absolute left-[15px] top-8 bottom-[-24px] w-[2px] bg-border-main/50"></div>
                                                )}
                                                <div className={`w-8 h-8 rounded-full border-4 border-background flex items-center justify-center z-10 shrink-0 ${i === 0 ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'bg-muted/10 text-muted'}`}>
                                                    <History className="w-3.5 h-3.5" />
                                                </div>
                                                <div className="space-y-1">
                                                    <p className="text-[10px] font-black text-foreground uppercase tracking-tight">{log.action}</p>
                                                    <p className="text-[9px] text-muted font-bold flex items-center gap-1 uppercase">
                                                        <User className="w-2.5 h-2.5" /> {log.user} • {log.time}
                                                    </p>
                                                    {log.note && <p className="text-[9px] text-primary font-medium italic mt-1 leading-tight">"{log.note}"</p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        <div className="text-center pb-4">
                                            <p className="text-[10px] font-black text-primary uppercase tracking-widest">Entri Jurnal Otomatis</p>
                                            <p className="text-xs font-bold text-muted">{selectedExpenditure.journal?.id}</p>
                                        </div>
                                        <div className="space-y-4">
                                            {selectedExpenditure.journal?.entries.map((entry, i) => (
                                                <div key={i} className={`p-4 rounded-2xl border border-border-main ${entry.debit > 0 ? 'bg-primary-soft/10' : 'bg-success-soft/10 ml-4'}`}>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="text-[9px] font-black uppercase text-muted tracking-tighter">Akun</span>
                                                        <span className="text-[9px] font-black uppercase text-primary bg-white px-2 py-0.5 rounded-full border border-border-main">{entry.debit > 0 ? 'Debit' : 'Kredit'}</span>
                                                    </div>
                                                    <p className="text-xs font-black text-foreground">{entry.account}</p>
                                                    <p className={`text-sm font-black mt-2 tabular-nums ${entry.debit > 0 ? 'text-primary' : 'text-success'}`}>
                                                        {formatCurrency(entry.debit > 0 ? entry.debit : entry.credit)}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="pt-4 border-t border-border-main text-center">
                                            <p className="text-[10px] text-muted font-medium italic">Data jurnal read-only. Diproduksi otomatis oleh sistem pada saat konfirmasi pembayaran.</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-8 border-t border-border-main bg-surface/50 gap-3 hidden md:flex">
                                <button onClick={() => setIsDetailModalOpen(false)} className="w-full py-4 bg-background border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-secondary-soft/50 transition-all">Tutup</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Pay Modal */}
            {isPayModalOpen && selectedExpenditure && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface p-8 rounded-[40px] shadow-2xl max-w-sm w-full space-y-8 animate-in zoom-in-95 duration-200">
                        <div className="text-center space-y-4">
                            <div className="w-20 h-20 bg-success-soft text-success rounded-3xl flex items-center justify-center mx-auto shadow-inner rotate-3">
                                <CheckCircle2 className="w-10 h-10" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-2xl font-black text-foreground tracking-tight">Konfirmasi Bayar?</h3>
                                <p className="text-xs text-muted font-medium leading-relaxed">
                                    Status akan diubah menjadi <b className="text-success uppercase">Selesai / Dibayar</b>.
                                    Sistem akan otomatis membuat <b className="text-primary uppercase">Jurnal Umum</b>.
                                </p>
                            </div>
                        </div>

                        <div className="bg-background rounded-3xl p-6 border border-border-main space-y-4">
                            <div className="flex items-center justify-between border-b border-border-main pb-4">
                                <span className="text-[10px] font-black text-muted uppercase">Penerima</span>
                                <span className="text-xs font-bold text-foreground">{selectedExpenditure.vendor}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-muted uppercase">Total Bayar</span>
                                <span className="text-base font-black text-primary tabular-nums">{formatCurrency(selectedExpenditure.amount)}</span>
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <button onClick={() => setIsPayModalOpen(false)} className="flex-1 py-4 bg-background border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-secondary-soft/50 transition-all">Batal</button>
                            <button onClick={() => handleStatusMove(selectedExpenditure.id, 'Selesai / Dibayar')} className="flex-1 py-4 bg-success text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:shadow-2xl hover:shadow-success/30 transition-all flex items-center justify-center gap-2">
                                Ya, Bayar <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </main>
    );
}

